import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { Broker_OrderType, Order, OrderStatus, OrderType, Product, TransactionType, Validity } from "../proto/generated/order";
import { Security } from "../proto/generated/security";
import { futureOrderDataType, optionTradeDataType } from "../pages/typings/options.typings";
import store from "../store";
import { Position } from "../proto/generated/position";
import { getSecurityAsync, getSecurityKey } from "../util/security";
import { setPredefinedStrategy } from "./strategyBuilder.reducer";
import { SelectedSqOffStrategyType } from '../components/postionsWidget'
import { v4 as uuid } from "uuid";
import { SlTargetType } from "../components/common/slTargetInput/slTargetInput";

export type OrdersDataType = {
	type: 'buy' | 'sell'
	security: Security | undefined
	readonly?: boolean
	expiry: number
	quantity: number
	orderDetails: Order | undefined
	uuid: string
	visibility: boolean
	timeStamp?: number
	traded?: boolean
	sqOffWithOrderId?: string
	sqOffByOrderId?: string
} & ({
	data: optionTradeDataType,
	strike: number,
	contractType: 'OPTION',
	callput: 'call' | 'put'
} | {
	data: futureOrderDataType,
	contractType: 'FUTURE'
})

export type OptionOrderDataType = Extract<OrdersDataType, { contractType: 'OPTION' }>
export type FutureOrderDataType = Extract<OrdersDataType, { contractType: 'FUTURE' }>


export interface OrderDataPrices {
	price: string
	topAsk: number
	topBid: number
	mid: number
	visibility: boolean
	timeStamp?: number
	traded?: boolean
}

export type OrderState = 'order' | 'square-off' | 'waiting' | 'waiting-square-off' | 'complete'



export interface QuickTradeData {
	openDialog: boolean,
	mode: 'regular' | 'trigger' | 'iceberg' | 'slOrTarget'
	type: 'trade' | 'modify' | 'notTradable'
	isSquareOffOrder: boolean,
	tradingAccountId?: string,
	addStopLoss: boolean,
	addTarget: boolean,
	stopLossType?: SlTargetType,
	targetType?: SlTargetType,
	stopLoss?: number,
	target?: number,
	maxQuantity: number // set as 0 for undefined,
	icebergLegs?: number, // number of legs for iceberg orders
	order: Partial<Order>
}

export interface ConnectTradingAccount {
	type: 'connect' | 'reconnect'
	openDialog: boolean
	tradingAccountId: string
}

export type BasketAnalyzeDetails = {
	id: string,
	strategyCode: string,
	basketPositions: Position[],
	underlyingSecurity: Security | undefined
	positionOrders: OrdersDataType[]
	positionParentSqrOffOrders: OrdersDataType[],
	positionOrderPrices: OrderDataPrices[],
	executedProfit: number
}

export interface OrdersStateType {
	orders: OrdersDataType[]
	hoveredOrderIndex: number[] | undefined
	parentSquareOffOrders: OrdersDataType[]
	orderType: Broker_OrderType
	product: Product
	ordersPrices: OrderDataPrices[]
	orderState: OrderState
	basketId: string
	batchId: string
	orderSecurityKey: string | undefined
	ordersHistory: Order[]	| undefined
	quickOrder?: QuickTradeData
	connectTradingAccount: ConnectTradingAccount
	basketAnalyzeDetails: BasketAnalyzeDetails | undefined
    selectedStrategy: SelectedSqOffStrategyType | undefined
	recentFiredOrderAlerts: (Order & {orderAlertId: string})[]
}

export const ordersInitialState: OrdersStateType = {
	orders: [],
	hoveredOrderIndex: undefined,
	parentSquareOffOrders: [],
	orderType: Broker_OrderType.MARKET_ORDER,
	product: Product.CNC,
	ordersPrices: [],
	orderState: 'order',
	basketId: '',
	batchId: '',
	orderSecurityKey: undefined,
	ordersHistory: undefined,
	quickOrder: {
		mode: 'regular',
		openDialog: false,
		type: 'trade',
		isSquareOffOrder: false,
		maxQuantity: 0,
		
		order: {
			orderType: OrderType.NORMAL,
			brokerOrderType: Broker_OrderType.MARKET_ORDER,
			transactionType: TransactionType.TRANSACTION_TYPE_UNKNOWN,
			product: Product.INTRADAY,
			security: undefined,
			price: undefined,
			quantity: 0,
			validity: Validity.DAY,
			disclosedQuantity: 0,
			marketProtection: 0,
		},
		addTarget: false,
		addStopLoss: false,
		stopLossType: "points",
		targetType: "points"
	},
	connectTradingAccount: {
		type: 'connect',
		openDialog: false,
		tradingAccountId: ''
	},
	basketAnalyzeDetails: undefined,
    selectedStrategy: undefined,
	recentFiredOrderAlerts: []
};

const ordersSlice = createSlice({
	name: 'security',
	initialState: ordersInitialState,
	reducers: {
		setOrderData(state, action) {
			state.orders = action.payload.orders
			state.orderSecurityKey = action.payload.security || state.orderSecurityKey
			if (action.payload.reset) {
				state.ordersPrices = []
				state.basketId = ''
				state.orderState = 'order'
				state.batchId = ''
				state.orderType = Broker_OrderType.MARKET_ORDER
				state.parentSquareOffOrders = []
				state.orderSecurityKey = ''
				state.hoveredOrderIndex = undefined
			}
			if (action.payload.orderPrices) {
				state.ordersPrices = action.payload.orderPrices
			}
		},
		setHoveredOrderIndex(state, action: PayloadAction<number[] | undefined>) {
			state.hoveredOrderIndex = action.payload
		},
		setParentSquareOffOrders(state, action) {
			state.parentSquareOffOrders = action.payload
		},
		setBasketAnalyzeDetails(state, action) {
			state.basketAnalyzeDetails = action.payload
			if(!action.payload) {
				state.orders = []
				state.ordersPrices = []
				state.basketId = ''
				state.orderState = 'order'
				state.batchId = ''
				state.orderType = Broker_OrderType.MARKET_ORDER
				state.parentSquareOffOrders = []
				state.orderSecurityKey = ''
				state.hoveredOrderIndex = undefined
			}
		},
		setOrderDetails(state, action) {
			if (state.orders.length > 0) {
				if (action.payload.initial) {
					if (!state.orders[action.payload.index].orderDetails?.status) {
						const newOrders = [...state.orders]
						newOrders[action.payload.index].orderDetails = action.payload.orderDetails
						state.orders = newOrders
						if (state.orders[action.payload.index].orderDetails?.status === OrderStatus.EXECUTED) {
							const price = (state.orders[action.payload.index].orderDetails?.averageTradedPrice ?? 0) / (state.orders[action.payload.index].security?.priceMultiplier ?? 100)
							state.ordersPrices[action.payload.index] = {
								...state.ordersPrices[action.payload.index],
								price: price.toFixed(2),
								mid: price,
								topAsk: price,
								topBid: price
							}
						}
					}
				} else {
					const newOrders = [...state.orders]
					newOrders[action.payload.index].orderDetails = action.payload.orderDetails
					state.orders = newOrders
					if (state.orders[action.payload.index].orderDetails?.status === OrderStatus.EXECUTED) {
						const price = (state.orders[action.payload.index].orderDetails?.averageTradedPrice ?? 0) / (state.orders[action.payload.index].security?.priceMultiplier ?? 100)
						state.ordersPrices[action.payload.index] = {
							...state.ordersPrices[action.payload.index],
							price: price.toFixed(2),
							mid: price,
							topAsk: price,
							topBid: price
						}
					}
				}
			}
		},
		setOrderType(state, action) {
			state.orderType = action.payload
		},
		setProductType(state: OrdersStateType, action: PayloadAction<Product>) {
			state.product = action.payload
		},
		setOrderPrices(state, action) {
			if (state.orders.length === 0) {
				state.ordersPrices = []
			} else {
				state.ordersPrices = action.payload
			}
		},
		setOrderState(state, action) {
			if (state.orders.length === 0) {
				state.orderState = 'order'
			} else {
				state.orderState = action.payload
			}
		},
		setBasketId(state, action) {
			state.basketId = action.payload?.basketId ?? ''
			state.batchId = action.payload?.batchId ?? ''
		},
		setOrderHistory(state, action: PayloadAction<{
			updates: Order[]
		}>) {
			state.ordersHistory = action.payload.updates.sort((a, b) => b.created - a.created)
		},
		updateOrderHistory(state, action: PayloadAction<{
			updates: Order[]
		}>) {
			let newOrders = state.ordersHistory ? [...state.ordersHistory] : []
			action.payload.updates.forEach((order) => {
				const index = newOrders.findIndex(o => o.orderId === order.orderId)
				if (index >= 0) {
					newOrders[index] = order
				} else {
					newOrders.unshift(order)
				}
			})
			newOrders.sort((a, b) => b.created - a.created)
			state.ordersHistory = newOrders;
		},
		/** Inserts orders not in history yet; never replaces one (a pushed copy is always newer). */
		addMissingOrders(state, action: PayloadAction<{
			orders: Order[]
		}>) {
			const known = new Set((state.ordersHistory ?? []).map(o => o.orderId))
			const missing = action.payload.orders.filter(o => o.orderId && !known.has(o.orderId))
			if (!missing.length) return
			state.ordersHistory = [...missing, ...(state.ordersHistory ?? [])].sort((a, b) => b.created - a.created)
		},
		updateOrder(state, action) {
			const updatedOrder = action.payload.order; 
			const newOrders = [...state.orders];
			const index = newOrders.findIndex(order => order.orderDetails?.orderId === updatedOrder.orderId);
			if (index >= 0) {
				state.orders[index].orderDetails = updatedOrder;
			}
		},
		setQuickOrderData(state, action: PayloadAction<Partial<QuickTradeData>>) {
			state.quickOrder = {
				...state.quickOrder,
				...action.payload,
				order: {
					...(state.quickOrder?.order || {}),
					...(action.payload.order || {})
				}
			}
		},
		removeQuickOrderData(state) {
			state.quickOrder = ordersInitialState.quickOrder
		},
		openConnectAccountDialog(state, action: PayloadAction<{
			type: 'connect' | 'reconnect',
			tradingAccountId: string
		}>) {
			state.connectTradingAccount.type = action.payload.type
			state.connectTradingAccount.tradingAccountId = action.payload.tradingAccountId
			state.connectTradingAccount.openDialog = true
		},
		closeConnectAccountDialog(state) {
			state.connectTradingAccount.openDialog = false
		},
        setSelectedStrategy(state, action: PayloadAction<SelectedSqOffStrategyType | undefined>) {
            state.selectedStrategy = action.payload
        },
		addOrderFiredAlert(state: OrdersStateType, action: PayloadAction<Order>) {
			return {
				...state,
				recentFiredOrderAlerts: [
					...state.recentFiredOrderAlerts,
					{
						orderAlertId: uuid(),
						...action.payload
					}
				]
			}
		},
		removeOrderFiredAlert(state: OrdersStateType, action: PayloadAction<string>) {
			const logId = action.payload;
			return {
				...state,
				recentFiredOrderAlerts: state.recentFiredOrderAlerts.filter(v => v.orderAlertId !== logId)
			}
		},
		removeAllOrderFiredAlerts(state: OrdersStateType) {
			return {
				...state,
				recentFiredOrderAlerts: []
			}
		},
    },
})

const { actions, reducer: ordersReducer } = ordersSlice

export const {
	setOrderData,
	setHoveredOrderIndex,
	setParentSquareOffOrders,
	setOrderType,
	setProductType,
	setOrderPrices,
	setOrderState,
	setBasketId,
	setOrderDetails,
	setOrderHistory,
	updateOrderHistory,
	addMissingOrders,
	setQuickOrderData,
	removeQuickOrderData,
	openConnectAccountDialog,
	closeConnectAccountDialog,
	setBasketAnalyzeDetails,
	updateOrder,
    setSelectedStrategy,
	addOrderFiredAlert,
	removeOrderFiredAlert,
	removeAllOrderFiredAlerts
} = actions

export const setOrderDataFunction = async ({ orders, token = '', resetPredefinedStrategy = true, resetOrderState = false, orderPrices = undefined, strategyName }: { orders: OrdersDataType[], token?: string, resetPredefinedStrategy?: boolean, resetOrderState?: boolean, orderPrices?: OrderDataPrices[], strategyName?: string }) => {
	if(strategyName) {
		store.dispatch(setPredefinedStrategy(strategyName))
	} else if (resetPredefinedStrategy) {
		store.dispatch(setPredefinedStrategy(undefined))
	}
	const primarySecurity = store.getState()?.homeStore?.primarySecurity
	let secKey = primarySecurity?.key
	if (primarySecurity?.expressionMode) {
		const sec = await getSecurityAsync(primarySecurity?.securities?.[0] ?? '')
		secKey = sec ? getSecurityKey(sec.exchange, sec.underlyingSecurityCode ?? '') : (secKey ?? '')
	}
	const newOrders: OrdersDataType[] = []
	for (const order of orders) {
		let newOrder = { ...order }
		if (newOrder.contractType === 'OPTION' && newOrder.data.callOption && newOrder.data.putOption) {
			const data = newOrder.callput === 'call' ? newOrder.data.callOption : newOrder.data.putOption
			newOrder = {
				...newOrder,
				security: data.security,
				expiry: data.security.expiry?.expiry ?? 0
			}
		} else if (newOrder.contractType === 'FUTURE' && newOrder.data.security) {
			const futureSecKey = getSecurityKey(newOrder.data.security?.exchange ?? 1, newOrder.data.security?.code ?? '')
			const security = await getSecurityAsync(futureSecKey)
			newOrder = {
				...newOrder,
				data: {
					...newOrder.data,
					security: security
				},
				security: security,
				expiry: security?.expiry?.expiry ?? 0
			}
		}
		newOrders.push(newOrder)
	}
	const storeData = store.getState().ordersStore as OrdersStateType
	const shouldReset = ['complete'].includes(storeData.orderState)
	store.dispatch(setOrderData({ orders: newOrders, security: token ? token : secKey, reset: resetOrderState || shouldReset || (orders && orders.length === 0 && storeData.basketAnalyzeDetails?.positionOrders.length===0 && storeData.parentSquareOffOrders.length===0), orderPrices: orderPrices }))
}

export default ordersReducer
